import type {
    AnchorProtocol,
    ApplicationId,
    BaseBreakPointProtocol,
    BlockId,
    CursorProtocol,
    EnvironmentId,
    FieldADTValue,
    FilterableProtocol,
    FilterFormType,
    PageId,
    RecordId,
    ScopeId,
    SortableProtocol,
    VariableADTvalue
} from '../../protocols'
import { BlockType } from '../Basic'
import { BaseBlock } from './BaseBlock'

export enum FilterBlockMode {
    'normal' = 'normal',
    'tile' = 'tile'
}

export enum FilterSelectWay {
    'singleSelect' = 'singleSelect',
    'multiSelect' = 'multiSelect'
}

export enum FilterWay {
    'textFilter' = 'textFilter',
    'numberFilter' = 'numberFilter',
    'dateFilter' = 'dateFilter',
    'personFilter' = 'personFilter',
    'selectFilter' = 'selectFilter',
    'cascadeFilter' = 'cascadeFilter',
    'boolFilter' = 'boolFilter'
}

export type NumberRangeArray = [number | undefined, number | undefined]

export type FilterBlockIdentify =
    | `${ApplicationId}-${EnvironmentId}-${PageId}-${RecordId}-${BlockId}`
    | `${ApplicationId}-${EnvironmentId}-${PageId}-${RecordId}-${ScopeId}-${BlockId}`
    | `${ApplicationId}-${EnvironmentId}-${PageId}-${BlockId}`

export type FilterBlockItemValue = {
    type: FilterWay
    value: string[]
    customValue?: NumberRangeArray
}

export type FilterBlockValue = Record<string, FilterBlockItemValue>

export type NumberOption = {
    id: string
    label: string
    value: [number | undefined, number | undefined]
}

export enum NumberRangeMode {
    'slider' = 'slider',
    'custom' = 'custom',
    'disable' = 'disable'
}

export type TextFilterConfig = Record<'filterWay', FilterWay.textFilter> &
    FilterableProtocol &
    SortableProtocol & {
        pointer?: string
        fieldPointer?: string
        showFieldPointer?: string
        textDefaultVariables?: VariableADTvalue
    }

export type NumberFilterConfig = Record<'filterWay', FilterWay.numberFilter> & {
    numberOptions?: NumberOption[]
    numberRangeMode?: NumberRangeMode
    numberRange?: [number | undefined, number | undefined]
    step?: number
}

export type DateFilterConfig = Record<'filterWay', FilterWay.dateFilter> & {
    dateVariables?: string[]
    dateDefaultVariables?: string[]
    isDateRange?: boolean
}

export type PersonFilterConfig = Record<'filterWay', FilterWay.personFilter> & FilterableProtocol & SortableProtocol

export type SelectFilterConfig = Record<'filterWay', FilterWay.selectFilter> & {
    pointer?: string
    fieldPointer?: string
    customOptions?: string[]
}

export type CascadeFilterConfig = Record<'filterWay', FilterWay.cascadeFilter> & {
    pointer?: string
    fieldPointer?: string
    parentFieldPointer?: string
    sortFieldPointer?: string
    showFieldPointer?: string
    isLastLevel?: boolean
    filter?: FilterFormType
}

export type BoolFilterConfig = Record<'filterWay', FilterWay.boolFilter>

export type InnerTypeFilterConfig =
    | TextFilterConfig
    | NumberFilterConfig
    | DateFilterConfig
    | PersonFilterConfig
    | SelectFilterConfig
    | CascadeFilterConfig
    | BoolFilterConfig

export type FilterBlockItemConfig = InnerTypeFilterConfig & {
    id: string
    title: string
    selectWay: FilterSelectWay
    filterWay: FilterWay
}

const getResolvedDefaultValues = (selectWay: FilterSelectWay, resolvedDefaultValue?: Pick<FieldADTValue, 'innerType' | 'value'>) => {
    if (!resolvedDefaultValue) {
        return
    }

    if (selectWay === FilterSelectWay.singleSelect) {
        if (resolvedDefaultValue.innerType !== 'TEXT' || typeof resolvedDefaultValue.value !== 'string') {
            return
        }

        return [resolvedDefaultValue.value]
    }

    if (resolvedDefaultValue.innerType !== 'ARRAY' || !Array.isArray(resolvedDefaultValue.value)) {
        return
    }

    return resolvedDefaultValue.value.filter((item): item is string => typeof item === 'string' && item !== '')
}

export const getFilterBlockItemDefaultValue = (
    config: FilterBlockItemConfig,
    optionValues?: string[],
    resolvedDefaultValue?: Pick<FieldADTValue, 'innerType' | 'value'>
): FilterBlockItemValue | undefined => {
    if (config.filterWay === FilterWay.dateFilter) {
        const dateVariables = Array.isArray(config.dateVariables)
            ? config.dateVariables.filter((item): item is string => typeof item === 'string')
            : undefined
        const dateDefaultVariables = Array.isArray(config.dateDefaultVariables)
            ? config.dateDefaultVariables.filter((item): item is string => typeof item === 'string')
            : undefined
        const defaultValues = dateDefaultVariables?.filter(item => dateVariables?.includes(item))
        if (!defaultValues?.length) {
            return
        }

        const validDefaultValues = optionValues ? defaultValues.filter(item => optionValues.includes(item)) : defaultValues
        if (!validDefaultValues.length) {
            return
        }

        const finalValues = config.selectWay === FilterSelectWay.singleSelect ? validDefaultValues.slice(0, 1) : validDefaultValues
        if (!finalValues.length) {
            return
        }

        return {
            type: config.filterWay,
            value: finalValues
        }
    }

    if (config.filterWay === FilterWay.textFilter) {
        const defaultValue = config.textDefaultVariables
        if (!defaultValue) {
            return
        }

        const defaultValues = getResolvedDefaultValues(config.selectWay, resolvedDefaultValue)
        if (!defaultValues?.length) {
            console.log('22222')
            return
        }

        if (optionValues && defaultValues.some(item => !optionValues.includes(item))) {
            console.log('333333')
            return
        }

        return {
            type: config.filterWay,
            value: defaultValues
        }
    }
}

export interface FilterBlockConfig extends BaseBreakPointProtocol, AnchorProtocol {
    mode: FilterBlockMode
    filterItems: FilterBlockItemConfig[]
    labelWidth?: number
    showLabel: boolean
    noFilterLabel?: string
    cursor?: CursorProtocol
}

export abstract class FilterBlockAbstract extends BaseBlock {
    type = BlockType.filter

    abstract override config: FilterBlockConfig
}
